import React, {useEffect, useState} from "react";
import {Container, Form} from "react-bootstrap";
import PurposeBox from "../../Components/SalesAnalysis/PurposeBox.jsx";
import StickyFilterBlock from "../../Components/StickyFilterBlock.jsx";
import {filterZusammenfassung} from "../../Components/SalesAnalysis/format.js";
import AnalysisFilterBar from "../../Components/SalesAnalysis/AnalysisFilterBar.jsx";
import DataStatusBar from "../../Components/SalesAnalysis/DataStatusBar.jsx";
import VertreterCustomerTable from "../../Components/SalesAnalysis/VertreterCustomerTable.jsx";
import useAnalysisFilters from "../../Components/SalesAnalysis/useAnalysisFilters.js";
import {useQueryState} from "../../Components/SalesAnalysis/useQueryState.js";
import SelectList from "../../Components/SelectList.jsx";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";

const POOL_CODE = "pool";

/**
 * Vertreter-Seite: Auswahl eines Vertreters (oder des Pools "Nicht
 * zugeordnet - prüfen") mit seiner Kundenrangliste. Die Vertreter-Auswahl
 * hier ist eine Einfachauswahl (welcher Vertreter wird betrachtet) - zu
 * unterscheiden vom Mehrfachfilter "Vertreter" der gemeinsamen Filterleiste,
 * der auf dieser Seite deshalb ausgeblendet bleibt.
 *
 * Angeboten werden nur Kürzel mit Daten (`has_data`, mindestens ein
 * zugeordneter Kunde mit Umsatz im Standardzeitraum) plus der Pool, in der
 * vom Server gelieferten Reihenfolge (schnittstelle.md 7.3); ohne `rep` in
 * der URL startet die Seite mit dem ersten Eintrag dieser Liste. Fehlt
 * `has_data` (Backend liefert es noch nicht), bleibt jeder Vertreter
 * wählbar - bisheriges Verhalten als Rückfall.
 */
export default function SalesAnalysisVertreter() {
    const filters = useAnalysisFilters();
    const [rep, setRep] = useQueryState("rep", undefined);
    const [reloadToken, setReloadToken] = useState(0);

    const repsWithData = (filters.meta?.reps || []).filter((r) => r.has_data !== false);

    useEffect(() => {
        if (!rep && repsWithData.length) {
            setRep(repsWithData[0].code);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.meta]);

    const repOptions = [
        ...repsWithData.map((r) => ({value: r.code, label: r.is_former ? `${r.name} (ehemalig)` : r.name})),
        {value: POOL_CODE, label: "Nicht zugeordnet – prüfen"},
    ];

    const params = {
        year_from: filters.params.year_from,
        year_to: filters.params.year_to,
        stichmonat: filters.params.stichmonat,
        sectors: filters.params.sectors,
        article_types: filters.params.article_types,
        product_lines: filters.params.product_lines,
        without_freight: filters.params.without_freight,
        rep,
        _v: reloadToken,
    };

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox />
            <h1 className="h4 mb-2">Vertreter</h1>
            {filters.metaError && <div className="alert alert-warning">{filters.metaError}</div>}
            {filters.filtersReady && (
                <>
                    <StickyFilterBlock summary={filterZusammenfassung(filters, [repOptions.find((o) => o.value === rep)?.label])}>
                        <AnalysisFilterBar filters={filters} showReps={false} />
                        <div className="mb-3" style={{maxWidth: 260}}>
                            <Form.Label className="small text-muted mb-1" htmlFor="va-rep">
                                <HeaderWithHelp help="Kundenverantwortlicher in weclapp, über den Benutzer dem Kürzel zugeordnet (Stammdaten).">
                                    Vertreter
                                </HeaderWithHelp>
                            </Form.Label>
                            <SelectList id="va-rep" size="sm" value={rep} options={repOptions} onChange={setRep} />
                        </div>
                        <DataStatusBar meta={filters.meta} onRecalculated={() => setReloadToken((t) => t + 1)} />
                    </StickyFilterBlock>
                    <VertreterCustomerTable filters={filters} params={params} isPool={rep === POOL_CODE} />
                </>
            )}
        </Container>
    );
}
